import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'terminos' | 'privacidad' | null;
interface LegalModalProps {
  doc: LegalDoc;
  onClose: () => void;
}
const TERMINOS = {
  title: 'Términos y Condiciones',
  body: [{
    h: '1. Naturaleza del servicio',
    p: 'Solución a Pensión es un servicio independiente de asesoría e información en materia pensional. No somos un fondo de pensiones, administradora de fondos de pensiones (AFP), ni entidad financiera vigilada por la Superintendencia Financiera de Colombia. Nuestros análisis tienen carácter informativo y orientador y no sustituyen la asesoría obligatoria que prestan las entidades del Sistema General de Pensiones.'
  }, {
    h: '2. Alcance del análisis',
    p: 'Las proyecciones, comparativas de regímenes y cálculos de brecha pensional se elaboran con base en la información que el usuario nos suministra (principalmente su historia laboral) y en la normatividad vigente al momento del análisis. Los resultados son estimaciones y pueden variar según cambios legales, decisiones de las entidades administradoras o información adicional no reportada por el usuario.'
  }, {
    h: '3. Obligaciones del usuario',
    p: 'El usuario se compromete a suministrar información veraz y completa. La calidad y exactitud del diagnóstico dependen directamente de la información aportada. Solución a Pensión no se hace responsable por conclusiones derivadas de datos incompletos o inexactos entregados por el usuario.'
  }, {
    h: '4. Pagos y entregas',
    p: 'Los servicios se prestan previo pago según el plan contratado. Los tiempos de entrega indicados son estimados y pueden depender de la disponibilidad y legibilidad de los documentos aportados.'
  }, {
    h: '5. Garantía',
    p: 'Si nuestro análisis no entrega información concreta y accionable sobre tu situación pensional real, devolvemos el 100% del valor pagado, conforme a las condiciones comunicadas al momento de la contratación.'
  }, {
    h: '6. Propiedad intelectual',
    p: 'Los informes, audios y documentos entregados son para uso personal del usuario. Su reproducción o distribución comercial sin autorización está prohibida.'
  }, {
    h: '7. Modificaciones',
    p: 'Solución a Pensión podrá actualizar estos términos en cualquier momento. La versión vigente será la publicada en www.solucionapension.com.'
  }]
};
const PRIVACIDAD = {
  title: 'Política de Privacidad y Tratamiento de Datos',
  body: [{
    h: '1. Responsable del tratamiento',
    p: 'Solución a Pensión es responsable del tratamiento de los datos personales que los usuarios suministran, en cumplimiento de la Ley 1581 de 2012 y sus decretos reglamentarios (Habeas Data) en Colombia.'
  }, {
    h: '2. Datos que recolectamos',
    p: 'Recolectamos los datos de contacto que nos proporcionas (nombre, número de WhatsApp, correo electrónico) y la información contenida en tu historia laboral necesaria para elaborar el análisis pensional solicitado.'
  }, {
    h: '3. Finalidad del tratamiento',
    p: 'Tus datos se utilizan exclusivamente para: (i) elaborar tu diagnóstico, auditoría o planificación pensional; (ii) comunicarnos contigo respecto al servicio; y (iii) entregarte los resultados. No vendemos ni compartimos tu información con terceros con fines comerciales.'
  }, {
    h: '4. Seguridad de la información',
    p: 'Aplicamos protocolos de seguridad técnicos y administrativos razonables para proteger tu información frente a accesos no autorizados, pérdida o alteración.'
  }, {
    h: '5. Derechos del titular',
    p: 'Como titular de tus datos, tienes derecho a conocer, actualizar, rectificar y suprimir tu información, así como a revocar la autorización otorgada. Puedes ejercer estos derechos escribiéndonos a hola@solucionapension.com.'
  }, {
    h: '6. Conservación',
    p: 'Conservamos tus datos durante el tiempo necesario para prestar el servicio y cumplir con obligaciones legales. Una vez cumplida la finalidad, procedemos a su eliminación segura cuando proceda.'
  }, {
    h: '7. Aceptación',
    p: 'Al compartir tu información con Solución a Pensión, autorizas el tratamiento de tus datos personales conforme a esta política.'
  }]
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (doc) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  const content = doc === 'terminos' ? TERMINOS : doc === 'privacidad' ? PRIVACIDAD : null;
  return <AnimatePresence data-id="element-110">
      {content && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-label={content.title} data-id="element-111">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.2
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-112">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 flex-shrink-0" data-id="element-113">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-114">
                {content.title}
              </h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400" aria-label="Cerrar" data-id="element-115">
                <X className="w-6 h-6" data-id="element-116" />
              </button>
            </div>
            <div className="px-6 py-6 overflow-y-auto space-y-6" data-id="element-117">
              {content.body.map((sec, i) => <div key={i} data-id="element-118">
                  <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-119">
                    {sec.h}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-sm" data-id="element-120">
                    {sec.p}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}